import React, { Fragment, useLayoutEffect, useRef, useState } from 'react'
import styled, { css } from 'styled-components'
import { object, string, bool, func, arrayOf } from 'prop-types'
import { useTranslation } from 'react-i18next'
import Tooltip, { TooltipWrapper } from '../shared/Tooltip2'

import { recognizeText, recognizeColor } from '../../lib/recognizeCategory'
import { mappingShape, dropdownValueShape } from '../../types'
import {
  TEXT_MEDIUM,
  TEXT_SMALL,
  WHITE,
  SHADOW_GREY,
  SILVER,
  VERMILION,
  RED,
  METAL,
} from '../../constants'
import { SECONDARY_BUTTON } from '../../constants/buttonThemes'
import capitalizeFirstLetter from '../../lib/capitalizeFirstLetter'

import Button from './Button'
import Text from './Text'
import Dropdown from './Dropdown'
import ErrorBoundary from './ErrorBoundary'
import MappingEditable from './MappingEditable'

// NOTE: We need to pass in min width to the tooltip
//  so it does not render outside the bounds of the preview area.
const tooltipContent = css`
  min-width: 0;
`

const keyVariants = {
  master: css`
    display: flex;
    justify-content: center;
    flex-direction: column;
    overflow: hidden;
    flex: 0 0 33%;
  `,
  partner: css`
    display: flex;
    justify-content: center;
    flex-direction: column;
    overflow: hidden;
    flex: 0 0 25%;
  `,
  outgoing: css`
    display: flex;
    justify-content: center;
    flex-direction: column;
    overflow: hidden;
    flex: 0 0 50%;
  `,
}

const valueVariants = {
  master: css`
    display: flex;
    flex: 0 0 33%;
  `,
  partner: css`
    display: flex;
    flex: 0 0 25%;
  `,
  outgoing: css`
    display: flex;
    width: 50%;
  `,
}

const previewVariants = {
  master: css`
    display: flex;
    align-items: center;
    flex: 1 1 auto;
    min-width: 0;
    word-break: break-word;
  `,
  partner: css`
    display: flex;
    align-items: center;
    flex: 1 1 auto;
    min-width: 0;
    word-break: break-word;
  `,
  outgoing: css`
    display: flex;
    align-items: center;
    flex: 1 1 auto;
    min-width: 0;
    word-break: break-word;
  `,
}

const KeyField = styled.div`
  ${({ variant }) => keyVariants[variant]};
`

const ValueField = styled.div`
  ${({ variant }) => valueVariants[variant]};
`

const PreviewField = styled.div`
  ${({ variant }) => previewVariants[variant]};
`

const ColumnTypeField = styled.div`
  display: flex;
  justify-content: center;
  flex-direction: column;
  overflow: hidden;
  flex: 0 0 25%;
`

const Wrapper = styled.div`
  background: ${WHITE};
  border-color: ${({ isRequiredAndEmpty }) =>
    isRequiredAndEmpty ? RED : SILVER};
  padding: 10px;
  box-shadow: ${SHADOW_GREY};
  display: flex;
  align-items: center;
  gap: 10px;
  border-bottom: 1px solid
    ${({ isRequiredAndEmpty }) => (isRequiredAndEmpty ? RED : SILVER)};
  border-top: 1px solid
    ${({ isRequiredAndEmpty }) => (isRequiredAndEmpty ? RED : 'transparent')};
  border-right: 1px solid
    ${({ isRequiredAndEmpty }) => (isRequiredAndEmpty ? RED : 'transparent')};
  border-left: 1px solid
    ${({ isRequiredAndEmpty }) => (isRequiredAndEmpty ? RED : 'transparent')};
`

const MappingStatus = styled.div`
  background-color: ${({ backgroundColor }) => backgroundColor};
  padding: 1px 3px;
  border-radius: 5px;
  display: inline-block;
  text-align: center;
  ${({ isAddPartner }) => isAddPartner && 'width: 100px'};
  ${({ isAddPartner }) => isAddPartner && 'margin: 0 auto'};
`

const PartnerButtonsContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  width: 25%;

  button {
    display: block;
  }
`

const DeletionButtonsContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  flex: 0 0 auto;

  button {
    display: block;
  }
`

const Row = ({
  columnTypeKeys,
  dataId,
  deleteMapping,
  dispatch,
  exampleValue,
  isMaster,
  isAddPartner,
  isOutgoing,
  isRequiredAndEmpty,
  keys,
  mapping,
  mappingRow,
  sourceColumn,
  updateMapping,
}) => {
  const wrapperRef = useRef(null)
  const [isRowEditable, setIsRowEditable] = useState(false)
  const [isPreviewTruncated, setIsPreviewTruncated] = useState(false)
  const { t } = useTranslation()

  // Monitor for truncation of the preview content.
  //  This flag is then consumed during rendering to only display the
  //  tooltip if it actually overflowed.
  useLayoutEffect(
    () => {
      const el = wrapperRef.current?.querySelector('[data-id^=previw-]')

      if (el) {
        setIsPreviewTruncated(el.scrollWidth > el.clientWidth)
      }
    },
    [exampleValue]
  )

  const capitalizedKeys = keys.map(key => ({
    id: key.id,
    name: capitalizeFirstLetter(key.name),
  }))

  const variant = isMaster ? 'master' : isAddPartner ? 'partner' : 'outgoing'

  const nameField = isMaster
    ? sourceColumn.replace('master_', '')
    : sourceColumn

  const mappingColumnField = isMaster
    ? mappingRow.options.name || ''
    : capitalizeFirstLetter(
        mappingRow.options.name
          ? mappingRow.options.name.replace('master_', '')
          : ''
      )

  const columnType = recognizeText(mappingRow.system, t)

  const isCustomColumn = mappingRow.system && mappingRow.system.custom === true

  return (
    <Fragment>
      {!isRowEditable && (
        <Wrapper
          isRequiredAndEmpty={isRequiredAndEmpty}
          data-id={dataId}
          ref={wrapperRef}
        >
          <ErrorBoundary>
            <KeyField variant={variant}>
              <div>
                <Text size={TEXT_MEDIUM} bold color={METAL}>
                  {capitalizeFirstLetter(nameField)}
                </Text>
                {!isAddPartner && (
                  <MappingStatus
                    backgroundColor={recognizeColor(mappingRow.system)}
                  >
                    <Text size={TEXT_SMALL} color={WHITE}>
                      {recognizeText(mappingRow.system, t)}
                    </Text>
                  </MappingStatus>
                )}
              </div>
            </KeyField>

            <ValueField
              variant={variant}
              data-id={`mapping-value-${mappingRow.options.name}`}
            >
              <Dropdown
                onChange={item =>
                  updateMapping(sourceColumn, item.id, mapping, dispatch)
                }
                options={[{ id: '', name: t('none') }, ...capitalizedKeys]}
                dataId={`mapping-value-${sourceColumn}`}
                placeholder={t('none')}
                placeholderColor={VERMILION}
                value={{
                  id: mappingRow.options.name || '',
                  name: mappingColumnField,
                }}
              />
            </ValueField>
            {isOutgoing && isCustomColumn && (
              <Fragment>
                <DeletionButtonsContainer>
                  <Button
                    style={{
                      visibility: isCustomColumn ? 'visible' : 'hidden',
                    }}
                    theme={SECONDARY_BUTTON}
                    onClick={() =>
                      deleteMapping(dispatch, mapping, sourceColumn)
                    }
                    dataId={`delete-${sourceColumn}`}
                  >
                    {t('button_delete')}
                  </Button>
                </DeletionButtonsContainer>
              </Fragment>
            )}
            {isMaster && (
              <Fragment>
                <PreviewField variant={variant}>
                  <Tooltip
                    title={isPreviewTruncated ? exampleValue : undefined}
                    customCss={tooltipContent}
                  >
                    <Text
                      ellipsis={true}
                      dataId={`previw-${exampleValue}`}
                      size={TEXT_SMALL}
                      color={METAL}
                    >
                      {exampleValue}
                    </Text>
                  </Tooltip>
                </PreviewField>
                {isCustomColumn && (
                  <DeletionButtonsContainer>
                    <Button
                      style={{
                        visibility: isCustomColumn ? 'visible' : 'hidden',
                      }}
                      theme={SECONDARY_BUTTON}
                      onClick={() =>
                        deleteMapping(dispatch, mapping, sourceColumn)
                      }
                      dataId={`delete-${sourceColumn}`}
                    >
                      {t('button_delete')}
                    </Button>
                  </DeletionButtonsContainer>
                )}
              </Fragment>
            )}
            {isAddPartner && (
              <Fragment>
                <ColumnTypeField>
                  <MappingStatus
                    isAddPartner
                    backgroundColor={recognizeColor(mappingRow.system)}
                  >
                    <Text
                      dataId={`mapping-row-${recognizeText(
                        mappingRow.system,
                        t
                      )}`}
                      size={TEXT_SMALL}
                      color={WHITE}
                    >
                      {recognizeText(mappingRow.system, t)}
                    </Text>
                  </MappingStatus>
                </ColumnTypeField>
                <PartnerButtonsContainer>
                  <Button
                    theme={SECONDARY_BUTTON}
                    onClick={() => setIsRowEditable(!isRowEditable)}
                    dataId={`edit-${sourceColumn}`}
                  >
                    {t('button_edit')}
                  </Button>
                  <Button
                    theme={SECONDARY_BUTTON}
                    onClick={() =>
                      deleteMapping(dispatch, mapping, sourceColumn)
                    }
                    dataId={`delete-${sourceColumn}`}
                  >
                    {t('button_delete')}
                  </Button>
                </PartnerButtonsContainer>
              </Fragment>
            )}
          </ErrorBoundary>
        </Wrapper>
      )}
      {isRowEditable && (
        <MappingEditable
          columnType={columnType}
          columnTypeKeys={columnTypeKeys}
          dispatch={dispatch}
          isAddPartner={isAddPartner}
          isOutgoing={isOutgoing}
          keys={keys}
          mapping={mapping}
          mappingField={mappingColumnField}
          name={nameField}
          setFormVisible={setIsRowEditable}
          t={t}
          updateMapping={updateMapping}
        />
      )}
    </Fragment>
  )
}

Row.propTypes = {
  columnTypeKeys: arrayOf(dropdownValueShape),
  dataId: string,
  deleteMapping: func,
  exampleValue: string,
  isAddPartner: bool,
  isMaster: bool,
  isOutgoing: bool,
  isRequiredAndEmpty: bool,
  keys: arrayOf(dropdownValueShape).isRequired,
  mapping: object.isRequired,
  mappingRow: mappingShape.isRequired,
  sourceColumn: string.isRequired,
  updateMapping: func.isRequired,
}

export default Row
